import React, { useState } from 'react';
import { Box, Button, Text, Heading, Input, FormControl, FormLabel, Divider, Code, ListItem, UnorderedList, Spinner } from '@chakra-ui/react';
import { testTemplateLoading, testFontAvailability } from '../utils/tests/templateTests';

const TemplateTestPage = () => {
  const [templateId, setTemplateId] = useState('0b789a88-6ea2-4a6f-a0bd-5e6ff95a6eac'); // Default to the Baseball template
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [fontResults, setFontResults] = useState(null);

  const handleTest = async () => {
    if (!templateId.trim()) {
      alert('Please enter a template ID');
      return;
    }

    setLoading(true);
    setResults(null);
    setFontResults(null);

    try {
      // Run the template loading test
      const testResults = await testTemplateLoading(templateId);
      setResults(testResults);

      // If we have font references, test their availability
      if (testResults.fontReferences && testResults.fontReferences.length > 0) {
        const fontTests = await testFontAvailability(testResults.fontReferences);
        setFontResults(fontTests);
      }
    } catch (error) {
      console.error('Test failed:', error);
      setResults({
        errors: [`Test execution failed: ${error.message}`]
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={6} maxWidth="800px" margin="0 auto">
      <Heading mb={4}>Template Test Tool</Heading>
      <Text mb={4}>
        This tool tests template loading and font availability to diagnose issues with corrupted templates.
      </Text>

      <FormControl mb={4}>
        <FormLabel>Template ID</FormLabel>
        <Input 
          value={templateId} 
          onChange={(e) => setTemplateId(e.target.value)}
          placeholder="Enter template ID" 
          width="100%"
        />
      </FormControl>

      <Button 
        colorScheme="blue" 
        onClick={handleTest} 
        isLoading={loading}
        mb={6}
      >
        Run Tests
      </Button>

      {loading && (
        <Box textAlign="center" my={8}>
          <Spinner size="xl" />
          <Text mt={2}>Running tests...</Text>
        </Box>
      )}

      {results && (
        <Box mt={4}>
          <Heading size="md" mb={2}>Test Results</Heading>
          
          {results.errors && results.errors.length > 0 && (
            <Box bg="red.50" p={3} borderRadius="md" mb={4}>
              <Heading size="sm" color="red.600" mb={2}>Errors</Heading>
              <UnorderedList>
                {results.errors.map((error, i) => (
                  <ListItem key={i} color="red.600">{error}</ListItem>
                ))}
              </UnorderedList>
            </Box>
          )}

          <Box bg="gray.50" p={4} borderRadius="md" mb={4}>
            <Text mb={1}><strong>Template exists:</strong> {results.templateExists ? '✅ Yes' : '❌ No'}</Text>
            <Text mb={1}><strong>Has scene URL:</strong> {results.hasSceneUrl ? '✅ Yes' : '❌ No'}</Text>
            <Text mb={1}><strong>Scene content loaded:</strong> {results.sceneContentLoaded ? '✅ Yes' : '❌ No'}</Text>
            <Text mb={1}><strong>Scene data valid:</strong> {results.sceneDataValid ? '✅ Yes' : '❌ No'}</Text>
            
            {results.fontReferences && (
              <>
                <Text mt={3} mb={1}><strong>Font references found:</strong> {results.fontReferences.length}</Text>
                {results.fontReferences.length > 0 && (
                  <Box maxH="200px" overflowY="auto" bg="gray.100" p={2} borderRadius="md">
                    <Code display="block" whiteSpace="pre" overflowX="auto">
                      {results.fontReferences.join('\n')}
                    </Code>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {fontResults && (
        <Box mt={6}>
          <Heading size="md" mb={2}>Font Availability</Heading>
          
          {fontResults.errors && fontResults.errors.length > 0 && (
            <Box bg="red.50" p={3} borderRadius="md" mb={4}>
              <Heading size="sm" color="red.600" mb={2}>Errors</Heading>
              <UnorderedList>
                {fontResults.errors.map((error, i) => (
                  <ListItem key={i} color="red.600">{error}</ListItem>
                ))}
              </UnorderedList>
            </Box>
          )}

          <Box bg="gray.50" p={4} borderRadius="md" mb={4}>
            <Text mb={2}>
              <strong>Fonts checked:</strong> {fontResults.fontsChecked}
            </Text>
            <Text mb={2}>
              <strong>Fonts available:</strong> {fontResults.fontsAvailable} of {fontResults.fontsChecked}
              {' '}({Math.round((fontResults.fontsAvailable / fontResults.fontsChecked) * 100)}%)
            </Text>
            
            <Divider my={3} />
            
            <Heading size="sm" mb={2}>Details</Heading>
            <Box maxH="300px" overflowY="auto" bg="gray.100" p={2} borderRadius="md">
              {fontResults.details.map((detail, i) => (
                <Box 
                  key={i} 
                  mb={2} 
                  p={2} 
                  borderRadius="md" 
                  bg={detail.available ? "green.50" : "red.50"}
                >
                  <Text fontWeight="bold">
                    {detail.fontPath}
                  </Text>
                  <Text fontSize="sm">
                    Status: {detail.available ? '✅ Available' : '❌ Not available'} 
                    {detail.status && ` (${detail.status})`}
                  </Text>
                  {detail.error && (
                    <Text fontSize="sm" color="red.600">
                      Error: {detail.error}
                    </Text>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TemplateTestPage; 